import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import { Loader2, Plus, Copy, DollarSign, Link2 } from 'lucide-react';

const codePreview = (name) => {
  const prefix = (name.split(' ')[0] || 'AMB').replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 8) || 'AMB';
  return `${prefix}##`;
};

const copyText = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied to clipboard`);
};

const NewCodeModal = ({ api, open, onClose, onCreated }) => {
  const empty = { code: '', ambassador_name: '', contact_email: '', handle: '', notes: '', bounty_amount: '20', grant_plan: 'none', grant_months: '0', max_redemptions: '', require_card: false };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.ambassador_name.trim() || !form.contact_email.trim()) {
      toast.error('Name and contact email are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim() || null,
        ambassador_name: form.ambassador_name,
        contact_email: form.contact_email,
        handle: form.handle || null,
        notes: form.notes || null,
        bounty_amount: parseFloat(form.bounty_amount) || 20,
        grant_plan: form.grant_plan,
        grant_months: parseInt(form.grant_months, 10) || 0,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
        require_card: form.grant_plan !== 'none' ? form.require_card : false,
        active: true,
      };
      const res = await api.post('/admin/ambassadors', payload);
      toast.success(`Code ${res.data.code} created`);
      setForm(empty);
      onCreated(res.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create code'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-ambassador-modal">
        <DialogHeader>
          <DialogTitle>New Ambassador Code</DialogTitle>
          <DialogDescription>Give an influencer a unique signup code and track what you owe them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Ambassador name *</Label>
              <Input value={form.ambassador_name} onChange={(e) => set('ambassador_name', e.target.value)} placeholder="Maya K" data-testid="amb-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Code (blank = auto)</Label>
              <Input value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder={codePreview(form.ambassador_name)} data-testid="amb-code-input" />
            </div>
          </div>
          {!form.code && form.ambassador_name && (
            <p className="text-xs text-muted-foreground" data-testid="amb-code-preview">Will generate: <span className="font-mono font-medium">{codePreview(form.ambassador_name)}</span> (## = random digits)</p>
          )}
          <div className="space-y-1">
            <Label>Contact email *</Label>
            <Input value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} placeholder="maya@example.com" data-testid="amb-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Handle</Label>
              <Input value={form.handle} onChange={(e) => set('handle', e.target.value)} placeholder="@maya.creates" data-testid="amb-handle-input" />
            </div>
            <div className="space-y-1">
              <Label>Bounty per activation ($)</Label>
              <Input type="number" value={form.bounty_amount} onChange={(e) => set('bounty_amount', e.target.value)} data-testid="amb-bounty-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Grant plan</Label>
              <Select value={form.grant_plan} onValueChange={(v) => set('grant_plan', v)}>
                <SelectTrigger data-testid="amb-grant-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Grant months</Label>
              <Input type="number" value={form.grant_months} onChange={(e) => set('grant_months', e.target.value)} disabled={form.grant_plan === 'none'} data-testid="amb-grant-months-input" />
            </div>
            <div className="space-y-1">
              <Label>Max uses</Label>
              <Input type="number" value={form.max_redemptions} onChange={(e) => set('max_redemptions', e.target.value)} placeholder="∞" data-testid="amb-max-input" />
            </div>
          </div>
          {form.grant_plan !== 'none' && (
            <div className="space-y-2 border rounded-lg p-3" data-testid="trial-mode-selector">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Grant mode</Label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="grant-mode" checked={!form.require_card} onChange={() => set('require_card', false)} className="mt-0.5" data-testid="grant-mode-nocard" />
                <span><strong>No card required</strong> — free {form.grant_months || 'N'} months, then drops to Free plan</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="grant-mode" checked={form.require_card} onChange={() => set('require_card', true)} className="mt-0.5" data-testid="grant-mode-card" />
                <span><strong>Card up front</strong> — free {form.grant_months || 'N'} month trial, then auto-renews at ${form.grant_plan === 'pro' ? '8' : '5'}/mo</span>
              </label>
              <p className="text-xs text-muted-foreground">No card = more signups. Card up front = fewer signups but far higher conversion.</p>
            </div>
          )}
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} className="min-h-[60px]" data-testid="amb-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="amb-create-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Create Code
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MarkPaidModal = ({ api, amb, onClose, onSaved }) => {
  const [activations, setActivations] = useState(String(amb.stats.owed_activations));
  const [amount, setAmount] = useState(String(amb.stats.amount_owed));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const n = parseInt(activations, 10);
    if (!n || n < 1) { toast.error('Activations covered must be at least 1'); return; }
    setSaving(true);
    try {
      await api.post(`/admin/ambassadors/${amb.code}/payout`, {
        activations_covered: n,
        amount: parseFloat(amount) || 0,
        note: note || null,
      });
      toast.success(`Payout recorded for ${amb.code}`);
      onSaved();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to record payout'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="mark-paid-modal">
        <DialogHeader>
          <DialogTitle>Mark Paid — {amb.code}</DialogTitle>
          <DialogDescription>
            This records a payment you've already sent. It does not send money.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Activations covered</Label>
            <Input type="number" value={activations} onChange={(e) => setActivations(e.target.value)} data-testid="payout-activations-input" />
          </div>
          <div className="space-y-1">
            <Label>Amount paid ($)</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payout-amount-input" />
          </div>
          <div className="space-y-1">
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Venmo 7/28" data-testid="payout-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="payout-confirm-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-1" />}
            Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditModal = ({ api, amb, onClose, onSaved }) => {
  const [form, setForm] = useState({
    ambassador_name: amb.ambassador_name, contact_email: amb.contact_email,
    handle: amb.handle || '', notes: amb.notes || '',
    bounty_amount: String(amb.bounty_amount), max_redemptions: amb.max_redemptions ? String(amb.max_redemptions) : '',
    require_card: !!amb.require_card,
    active: amb.active,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/ambassadors/${amb.code}`, {
        ambassador_name: form.ambassador_name,
        contact_email: form.contact_email,
        handle: form.handle,
        notes: form.notes,
        bounty_amount: parseFloat(form.bounty_amount) || 0,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : 0,
        require_card: form.require_card,
        active: form.active,
      });
      toast.success('Ambassador updated');
      onSaved();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-ambassador-modal">
        <DialogHeader>
          <DialogTitle>Edit — {amb.code}</DialogTitle>
          <DialogDescription>The code itself can't be changed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Name</Label><Input value={form.ambassador_name} onChange={(e) => set('ambassador_name', e.target.value)} data-testid="edit-amb-name" /></div>
            <div className="space-y-1"><Label>Contact email</Label><Input value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} data-testid="edit-amb-email" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label>Handle</Label><Input value={form.handle} onChange={(e) => set('handle', e.target.value)} data-testid="edit-amb-handle" /></div>
            <div className="space-y-1"><Label>Bounty ($)</Label><Input type="number" value={form.bounty_amount} onChange={(e) => set('bounty_amount', e.target.value)} data-testid="edit-amb-bounty" /></div>
            <div className="space-y-1"><Label>Max uses</Label><Input type="number" value={form.max_redemptions} onChange={(e) => set('max_redemptions', e.target.value)} placeholder="∞" data-testid="edit-amb-max" /></div>
          </div>
          <div className="space-y-1"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} className="min-h-[60px]" data-testid="edit-amb-notes" /></div>
          {amb.grant_plan && amb.grant_plan !== 'none' && (
            <div className="space-y-2 border rounded-lg p-3" data-testid="edit-trial-mode-selector">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Grant mode</Label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="edit-grant-mode" checked={!form.require_card} onChange={() => set('require_card', false)} className="mt-0.5" data-testid="edit-grant-mode-nocard" />
                <span><strong>No card required</strong> — free {amb.grant_months} months, then drops to Free plan</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="edit-grant-mode" checked={form.require_card} onChange={() => set('require_card', true)} className="mt-0.5" data-testid="edit-grant-mode-card" />
                <span><strong>Card up front</strong> — free {amb.grant_months} month trial, then auto-renews at ${amb.grant_plan === 'pro' ? '8' : '5'}/mo</span>
              </label>
              <p className="text-xs text-muted-foreground">No card = more signups. Card up front = fewer signups but far higher conversion.</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input type="checkbox" id="amb-active" checked={form.active} onChange={(e) => set('active', e.target.checked)} data-testid="edit-amb-active" />
            <Label htmlFor="amb-active">Active (accepting new signups)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-amb-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AmbassadorRow = ({ amb, api, onChanged, onMarkPaid, onEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState(null);
  const s = amb.stats;

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && users === null) {
      try {
        const res = await api.get(`/admin/ambassadors/${amb.code}/users`);
        setUsers(res.data.users);
      } catch { setUsers([]); }
    }
  };

  return (
    <Card className={!amb.active ? 'opacity-60' : ''} data-testid={`ambassador-row-${amb.code}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button className="text-left flex-1 min-w-0" onClick={toggle} data-testid={`ambassador-expand-${amb.code}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono font-bold text-sm">{amb.code}</span>
              <span className="text-sm text-muted-foreground truncate">{amb.ambassador_name}{amb.handle ? ` · ${amb.handle}` : ''}</span>
              {amb.grant_plan && amb.grant_plan !== 'none' && (
                <Badge variant="secondary" className="text-[10px]" data-testid={`grant-mode-badge-${amb.code}`}>
                  {amb.require_card ? 'Card up front' : 'No card'}
                </Badge>
              )}
              {!amb.active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
            </div>
            <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
              <span>{s.signups} signups</span>
              <span>{s.activated} activated</span>
              <span>{s.owed_activations} owed</span>
            </div>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`font-mono font-bold ${s.amount_owed > 0 ? 'text-primary text-lg' : 'text-muted-foreground text-sm'}`} data-testid={`ambassador-owed-${amb.code}`}>
              ${s.amount_owed.toFixed(2)}
            </span>
            {s.owed_activations > 0 && (
              <Button size="sm" onClick={() => onMarkPaid(amb)} data-testid={`mark-paid-btn-${amb.code}`}>Mark Paid</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onEdit(amb)} data-testid={`edit-amb-btn-${amb.code}`}>Edit</Button>
          </div>
        </div>
        {expanded && (
          <div className="mt-4 space-y-4 border-t pt-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-xs text-muted-foreground">Verified</p><p className="font-mono">{s.verified}</p></div>
              <div><p className="text-xs text-muted-foreground">Invoiced</p><p className="font-mono">{s.invoiced}</p></div>
              <div><p className="text-xs text-muted-foreground">Subscribers</p><p className="font-mono">{s.subscribers}</p></div>
              <div><p className="text-xs text-muted-foreground">Payment volume</p><p className="font-mono">${s.payment_volume.toFixed(2)}</p></div>
              <div><p className="text-xs text-muted-foreground">Redemptions</p><p className="font-mono">{s.signups}{amb.max_redemptions ? ` / ${amb.max_redemptions}` : ' / ∞'}</p></div>
              <div><p className="text-xs text-muted-foreground">Bounty</p><p className="font-mono">${amb.bounty_amount.toFixed(2)}</p></div>
              <div><p className="text-xs text-muted-foreground">Activations paid</p><p className="font-mono">{s.activations_paid}</p></div>
              <div><p className="text-xs text-muted-foreground">Created</p><p className="font-mono">{amb.created_at?.slice(0, 10)}</p></div>
              {amb.require_card && (
                <>
                  <div data-testid={`trials-started-${amb.code}`}><p className="text-xs text-muted-foreground">Trials started</p><p className="font-mono">{s.trials_started}</p></div>
                  <div data-testid={`trials-converted-${amb.code}`}><p className="text-xs text-muted-foreground">Trials converted</p><p className="font-mono">{s.trials_converted}</p></div>
                </>
              )}
            </div>
            {amb.notes && <p className="text-xs text-muted-foreground italic">{amb.notes}</p>}
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => copyText(amb.code, 'Code')} className="gap-1.5" data-testid={`copy-code-btn-${amb.code}`}>
                <Copy className="w-3.5 h-3.5" /> Copy code
              </Button>
              <Button size="sm" variant="outline" onClick={() => copyText(amb.signup_url, 'Signup link')} className="gap-1.5" data-testid={`copy-link-btn-${amb.code}`}>
                <Link2 className="w-3.5 h-3.5" /> Copy signup link
              </Button>
            </div>
            {(amb.payouts || []).length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Payout history</p>
                <div className="space-y-1">
                  {amb.payouts.map((p, i) => (
                    <p key={p.payout_id || i} className="text-xs text-muted-foreground" data-testid={`payout-row-${amb.code}-${i}`}>
                      {p.paid_at?.slice(0, 10)} — {p.activations_covered} activation{p.activations_covered !== 1 ? 's' : ''} · ${Number(p.amount).toFixed(2)}{p.note ? ` · ${p.note}` : ''}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Referred users</p>
              {users === null ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : users.length === 0 ? (
                <p className="text-xs text-muted-foreground">No signups yet.</p>
              ) : (
                <div className="space-y-1">
                  {users.map((u, i) => (
                    <p key={i} className="text-xs font-mono text-muted-foreground">
                      {u.email_masked} · {u.signed_up?.slice(0, 10)} · {u.verified ? 'verified' : 'unverified'} · {u.stripe_connected ? 'stripe ✓' : 'no stripe'} · {u.invoiced ? 'invoiced' : 'no invoices'} · {u.plan}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const AmbassadorsTab = ({ api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [payingAmb, setPayingAmb] = useState(null);
  const [editingAmb, setEditingAmb] = useState(null);

  const fetchAll = async () => {
    try {
      const res = await api.get('/admin/ambassadors');
      setData(res.data);
    } catch {
      toast.error('Failed to load ambassadors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const t = data?.totals || {};
  return (
    <div className="space-y-4" data-testid="admin-ambassadors">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground" data-testid="ambassador-summary">
          Total owed: <span className="font-mono font-bold text-foreground">${(t.total_owed || 0).toFixed(2)}</span>
          {' · '}Active codes: <span className="font-mono">{t.active_codes || 0}</span>
          {' · '}Total activations: <span className="font-mono">{t.total_activations || 0}</span>
          {' · '}Attributed payment volume: <span className="font-mono">${(t.payment_volume || 0).toFixed(2)}</span>
        </p>
        <Button size="sm" onClick={() => setShowNew(true)} className="gap-1.5" data-testid="new-ambassador-btn">
          <Plus className="w-4 h-4" /> New code
        </Button>
      </div>
      {(data?.ambassadors || []).length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground text-sm" data-testid="ambassadors-empty">No ambassador codes yet. Create one to start tracking influencer referrals.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {data.ambassadors.map((amb) => (
            <AmbassadorRow key={amb.code} amb={amb} api={api} onChanged={fetchAll} onMarkPaid={setPayingAmb} onEdit={setEditingAmb} />
          ))}
        </div>
      )}
      <NewCodeModal api={api} open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); fetchAll(); }} />
      {payingAmb && <MarkPaidModal api={api} amb={payingAmb} onClose={() => setPayingAmb(null)} onSaved={() => { setPayingAmb(null); fetchAll(); }} />}
      {editingAmb && <EditModal api={api} amb={editingAmb} onClose={() => setEditingAmb(null)} onSaved={() => { setEditingAmb(null); fetchAll(); }} />}
    </div>
  );
};
